import { CaretDownOutlined, CheckOutlined, SearchOutlined } from '@vindr/icons';
import { ParamsGetAssigns } from '@vindr/services/apis/assign.api';
import { extendDateOptions } from '@vindr/ui/src/components/ui/DateRangePicker';
import { Button, Row, Select } from 'antd';
import { memo, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FILTER_TYPE, INITIAL_FILTER_ASSIGNED } from './constants';
import useSearchOptions from './useSearchOptions';

const { Option } = Select;

function Filter({
  initFilters,
  onChange,
  forceSearch,
}: {
  initFilters: ParamsGetAssigns;
  onChange: (val: ParamsGetAssigns) => void;
  forceSearch: (params: ParamsGetAssigns) => void;
}) {
  const { t } = useTranslation('Vindoc');
  const [params, setParams] = useState<ParamsGetAssigns>(initFilters);

  useEffect(() => {
    //? set default institution
    setParams(initFilters);
    onChange(initFilters);
  }, [JSON.stringify(initFilters)]);

  const handleOnChangeFilter = (p, type, values, triggerSearch = false) => {
    if (!type) return;
    let tempFilterData = {};

    switch (type) {
      case FILTER_TYPE.ASSIGNED_AT:
        tempFilterData = {
          ...p,
          assigned_at_from: values[0].toISOString(),
          assigned_at_to: values[1].toISOString(),
        };
        break;
      default:
        tempFilterData = {
          ...p,
          [type]: values,
        };
        break;
    }
    setIsFiltered(true);
    if (triggerSearch) {
      onSearch(tempFilterData, type);
    } else {
      onChangeParams(tempFilterData);
    }
  };

  const SEARCH_OPTIONS = useSearchOptions(params, handleOnChangeFilter);

  // [0: Institution, 1: Scheduled Date, 2: Visit ID, 3: Patient Name, 4: Order Group, 5: Status, 6: Patient ID, 7: Study Description]
  const INITIAL_SEARCH_FIELDS = [1, 3, 5, 2, 0];

  const [searchFields, setSearchFields] = useState([...INITIAL_SEARCH_FIELDS]);
  const [isFiltered, setIsFiltered] = useState(false);

  const onChangeParams = useCallback((p) => {
    setParams(p);
  }, []);

  const onSearch = useCallback((p, type) => {
    setParams(p);
    onChange(p, type);
  }, []);

  // const handleOnSearch = () => {
  //   setIsFiltered(true);
  //   onSearch({ ...params });
  // };

  const handleChangeSearchKey = (key, replacekey) => {
    const tempParams = { ...params };

    if (key === FILTER_TYPE.ASSIGNED_AT) {
      delete tempParams.assigned_at_from;
      delete tempParams.assigned_at_to;
    } else delete tempParams[key];

    if (replacekey === FILTER_TYPE.ASSIGNED_AT) {
      tempParams.assigned_at_from = extendDateOptions['Today'][0].toISOString();
      tempParams.assigned_at_to = extendDateOptions['Today'][1].toISOString();
    }

    setIsFiltered(true);
    onChangeParams(tempParams);

    let tempSearchField = [...searchFields];
    const currentItemIndex = SEARCH_OPTIONS.findIndex((it) => it.key == key);
    const replaceItemIndex = SEARCH_OPTIONS.findIndex((it) => it.key == replacekey);
    tempSearchField = tempSearchField.map((it) => (it == currentItemIndex ? replaceItemIndex : it));
    setSearchFields(tempSearchField);
  };

  const handleClearAllFilters = useCallback(() => {
    setSearchFields(INITIAL_SEARCH_FIELDS);
    setParams({ ...initFilters });
    onChange({ ...INITIAL_FILTER_ASSIGNED });
    setIsFiltered(false);
  }, [initFilters]);

  return (
    <Row className="ml-[-7px] space-y-2 pt-3 pb-6" align="bottom">
      <div>
        <Row className="space-y-2" align="bottom">
          {searchFields &&
            SEARCH_OPTIONS &&
            searchFields.map((idx) => {
              const field = SEARCH_OPTIONS[idx];
              const options = SEARCH_OPTIONS.filter(
                (_, i) => i == idx || !searchFields.includes(i)
              );
              const Component = field?.component;

              return (
                <div key={field?.key} className="search-item">
                  <Row>
                    <div>
                      <Select
                        size="small"
                        className="select-dropdown-light border-0"
                        popupClassName="min-w-40"
                        menuItemSelectedIcon={<CheckOutlined />}
                        suffixIcon={<CaretDownOutlined />}
                        value={field.key}
                        onChange={(value) => handleChangeSearchKey(field.key, value)}
                      >
                        {options.map((item, idx) => (
                          <Option key={idx} value={item.key}>
                            {t(item.text)}
                          </Option>
                        ))}
                      </Select>
                    </div>
                  </Row>
                  <Row className="pl-[7px]">
                    <div>{Component ? Component : null}</div>
                  </Row>
                </div>
              );
            })}
        </Row>
      </div>
      <div className="ml-[7px] mr-1">
        <Row>
          <div className="mr-[7px]">
            <Button
              type="primary"
              className="flex items-center"
              size="small"
              // type="primary"
              icon={<SearchOutlined />}
              onClick={() => forceSearch?.(params)}
            >
              {t('Search')}
            </Button>
          </div>
          <div className="mr-0">
            <Button
              className="filter-btn"
              size="small"
              disabled={!isFiltered}
              // icon={<ReloadOutlined />}
              onClick={() => handleClearAllFilters()}
            >
              {t('Clear Filter')}
            </Button>
          </div>
        </Row>
      </div>
    </Row>
  );
}

Filter.defaultProps = {
  formatDate: 'YYYYMMDD',
};

export default memo(Filter);
